import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { colors } from "../../theme/colors";
import { isLobbyV2Path } from "../../universal/v2Routes";
import MatchSearchLoader from "../common/MatchSearchLoader";

const MotionBox = motion.create(Box);

export function isMatchmakingSearching(catalog = {}) {
    return (
        catalog?.lobbyState === "searching" ||
        Boolean(catalog?.matchmakingStarted)
    );
}

export function shouldShowPersistentQueue(catalog, pathname) {
    return isMatchmakingSearching(catalog) && !isLobbyV2Path(pathname);
}

export function getQueueElapsedSeconds(timestamp, nowMs = Date.now()) {
    let startedAt = Number(timestamp);
    const currentTimeMs = Number(nowMs);
    if (
        !Number.isFinite(startedAt) ||
        startedAt <= 0 ||
        !Number.isFinite(currentTimeMs) ||
        currentTimeMs <= 0
    ) {
        return null;
    }
    if (startedAt > 1e12) startedAt = Math.floor(startedAt / 1000);
    return Math.max(0, Math.floor(currentTimeMs / 1000) - startedAt);
}

export function formatQueueElapsed(elapsedSeconds) {
    if (!Number.isFinite(elapsedSeconds)) return "--:--";
    const minutes = Math.floor(elapsedSeconds / 60)
        .toString()
        .padStart(2, "0");
    const seconds = Math.floor(elapsedSeconds % 60)
        .toString()
        .padStart(2, "0");
    return `${minutes}:${seconds}`;
}

export function getQueueModeLabel(catalog = {}) {
    const partySize = Array.isArray(catalog?.currentLobby?.members)
        ? catalog.currentLobby.members.length
        : 0;
    return catalog?.currentLobbyCode && partySize > 1
        ? `PARTY · ${partySize}`
        : "SOLO";
}

export function canCancelPersistentQueue(catalog = {}) {
    const userId = catalog?.user?.userId;
    const currentLobbyCode = catalog?.currentLobbyCode;
    if (!currentLobbyCode) return true;
    const hostId = catalog?.currentLobby?.hostId;
    return Boolean(userId && hostId && String(userId) === String(hostId));
}

export default function PersistentQueueStatus({ catalog = {}, onCancel }) {
    const history = useHistory();
    const reduceMotion = useReducedMotion();
    const startedAt = catalog?.user?.playerQueueActionStartTs;
    const [nowMs, setNowMs] = useState(null);
    const canCancel = canCancelPersistentQueue(catalog);

    useEffect(() => {
        const tick = () => setNowMs(Date.now());
        tick();
        const intervalId = window.setInterval(tick, 1000);
        return () => window.clearInterval(intervalId);
    }, [startedAt]);

    const elapsedSeconds = getQueueElapsedSeconds(startedAt, nowMs);
    const elapsedLabel = formatQueueElapsed(elapsedSeconds);
    const modeLabel = getQueueModeLabel(catalog);
    const accessibleDuration = Number.isFinite(elapsedSeconds)
        ? `${elapsedSeconds} seconds`
        : "an unknown duration";

    const showCancel = canCancel && typeof onCancel === "function";

    return (
        <MotionBox
            initial={reduceMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{
                duration: reduceMotion ? 0 : 0.22,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
                minWidth: { xs: "11.25rem", sm: "14.25rem", lg: "15rem" },
                height: { xs: "3.55rem", sm: "4rem", xl: "4.15rem" },
                px: { xs: "0.72rem", sm: "0.9rem" },
                display: "flex",
                alignItems: "center",
                gap: { xs: "0.55rem", sm: "0.7rem" },
                borderRadius: "14px",
                position: "relative",
                border: "1px solid rgba(238,174,67,0.34)",
                background:
                    "linear-gradient(180deg, rgba(29,26,22,0.985), rgba(11,11,14,0.99))",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                boxShadow:
                    "inset 0 1px 0 rgba(255,232,188,0.09), 0 0 0 1px rgba(232,163,61,0.045), 0 16px 38px rgba(0,0,0,0.58), 0 5px 20px rgba(232,163,61,0.1)",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "1.1rem",
                    right: "1.1rem",
                    height: "1px",
                    background:
                        "linear-gradient(90deg, transparent, rgba(255,205,116,0.72), transparent)",
                    pointerEvents: "none",
                },
                color: colors.text.primary,
                overflow: "hidden",
                transition:
                    "border-color 200ms ease, background 200ms ease, box-shadow 200ms ease",
                "&:hover": {
                    borderColor: "rgba(242,183,82,0.5)",
                    background:
                        "linear-gradient(180deg, rgba(36,30,22,0.99), rgba(13,12,12,0.99))",
                    boxShadow:
                        "inset 0 1px 0 rgba(255,232,188,0.12), 0 0 0 1px rgba(232,163,61,0.07), 0 19px 44px rgba(0,0,0,0.64), 0 6px 24px rgba(232,163,61,0.14)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                },
            }}
        >
            <ButtonBase
                type="button"
                aria-label={`Matchmaking active. Searching for ${accessibleDuration}. Return to lobby.`}
                onClick={() => history.push("/lobby-v2")}
                sx={{
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "0.72rem", sm: "0.9rem" },
                    borderRadius: "8px",
                    color: "inherit",
                    "&.Mui-focusVisible": {
                        outline: `2px solid ${colors.border.focus}`,
                        outlineOffset: "2px",
                    },
                }}
            >
                <MatchSearchLoader size="compact" diameter={32} />

                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                        component="span"
                        sx={{
                            color: colors.accent.soft,
                            fontSize: { xs: "0.61rem", sm: "0.68rem" },
                            fontWeight: 750,
                            lineHeight: 1,
                            letterSpacing: "0.13em",
                        }}
                    >
                        SEARCHING
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            mt: "0.22rem",
                            color: colors.text.primary,
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: { xs: "0.94rem", sm: "1.06rem" },
                            fontWeight: 650,
                            lineHeight: 1,
                            letterSpacing: "0.035em",
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {elapsedLabel}
                    </Typography>
                </Box>
            </ButtonBase>

            {showCancel ? (
                <ButtonBase
                    type="button"
                    aria-label="Cancel search"
                    onClick={(event) => {
                        event.stopPropagation();
                        onCancel();
                    }}
                    sx={{
                        flexShrink: 0,
                        height: { xs: "1.95rem", sm: "2.15rem" },
                        px: { xs: "0.62rem", sm: "0.75rem" },
                        borderRadius: "8px",
                        border: "1px solid rgba(180, 70, 70, 0.5)",
                        background:
                            "linear-gradient(180deg, rgba(140, 32, 32, 0.92) 0%, rgba(92, 18, 18, 0.94) 100%)",
                        color: colors.text.primary,
                        fontSize: { xs: "0.58rem", sm: "0.64rem" },
                        fontWeight: 750,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                        "&:hover": {
                            borderColor: "rgba(220, 90, 90, 0.62)",
                            background:
                                "linear-gradient(180deg, rgba(160, 40, 40, 0.96) 0%, rgba(104, 20, 20, 0.96) 100%)",
                        },
                        "&.Mui-focusVisible": {
                            outline: `2px solid ${colors.border.focus}`,
                            outlineOffset: "2px",
                        },
                    }}
                >
                    Cancel
                </ButtonBase>
            ) : null}

            <Box
                sx={{
                    display: { xs: "none", sm: "block" },
                    width: "1px",
                    height: "1.85rem",
                    ml: "auto",
                    backgroundColor: colors.border.default,
                }}
            />
            <Typography
                component="span"
                sx={{
                    display: { xs: "none", sm: "block" },
                    color: colors.text.secondary,
                    fontSize: "0.69rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: "0.09em",
                    whiteSpace: "nowrap",
                }}
            >
                {modeLabel}
            </Typography>
        </MotionBox>
    );
}

PersistentQueueStatus.propTypes = {
    catalog: PropTypes.object,
    onCancel: PropTypes.func,
};
